/* eslint-disable react/prop-types */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import {
  fetchEventReservations,
  updateEventReservationArchive,
  updateEventReservationStatus,
} from "../../../services/EventReservationService";
import { updateEvent } from "../../../services/EventService";
import { fetchConnectedUser } from "../../../services/OrdersService";
import socket from "../../../services/socket";
import { promptEventFullClientOptions, resetEventFullClientOptions } from "./eventFullClientOptions";
import { confirmReservationStatusChange, showEventActionSuccess } from "./eventDialogs";
import "./events.css";
import "./eventReservations.css";

const PAGE_SIZE = 15;

const STATUS_OPTIONS = ["pending", "contacted", "confirmed", "cancelled"];

const RESERVATION_STATUS_FILTER_OPTIONS = [
  { id: "all", labelKey: "eventsFilterAll" },
  { id: "pending", labelKey: "eventReservationStatus_pending" },
  { id: "contacted", labelKey: "eventReservationStatus_contacted" },
  { id: "confirmed", labelKey: "eventReservationStatus_confirmed" },
  { id: "cancelled", labelKey: "eventReservationStatus_cancelled" },
];

function ResTableTh({ icon, label, iconClassName = "" }) {
  return (
    <span className="ev-res-th-label">
      <i className={`${icon} ev-res-th-label__icon${iconClassName ? ` ${iconClassName}` : ""}`} aria-hidden />
      <span>{label}</span>
    </span>
  );
}

const formatDateRange = (start, end) => {
  const opts = { day: "2-digit", month: "short", year: "numeric" };
  return `${new Date(start).toLocaleDateString(undefined, opts)} – ${new Date(end).toLocaleDateString(undefined, opts)}`;
};

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatReservationSlot = (dateValue, timeValue) => {
  if (!dateValue) return { dateLine: "—", timeLine: "" };
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return { dateLine: "—", timeLine: "" };
  const dateLine = date.toLocaleDateString(undefined, {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return { dateLine, timeLine: timeValue || "" };
};

const getInitials = (firstName, lastName) => {
  const first = String(firstName || "").trim().charAt(0);
  const last = String(lastName || "").trim().charAt(0);
  return (first + last).toUpperCase() || "?";
};

const normalizeWhatsAppNumber = (value) => String(value || "").replace(/\D/g, "");

const buildWhatsAppHref = (whatsapp, message) => {
  const digits = normalizeWhatsAppNumber(whatsapp);
  if (!digits) return "";
  const params = new URLSearchParams({ phone: digits });
  if (message) params.set("text", message);
  return `https://web.whatsapp.com/send?${params.toString()}`;
};

function PreferredChannelLabel({ channel, preferredContact, t }) {
  if (preferredContact !== channel) return null;
  return (
    <span className="ev-res-contact__preferred-label">{t("eventReservationColPreferred")}</span>
  );
}

function PreferredStar({ channel, preferredContact, t }) {
  if (preferredContact !== channel) return null;
  const label = t(`eventReservationPreferred_${channel}`);
  return (
    <i
      className="fa-solid fa-star ev-res-contact__preferred-star"
      title={label}
      aria-label={label}
    />
  );
}

function getGuestContactLine(reservation) {
  if (reservation?.email) {
    return { channel: "email", value: reservation.email };
  }
  if (reservation?.whatsapp) {
    return { channel: "whatsapp", value: reservation.whatsapp };
  }
  if (reservation?.phone) {
    return { channel: "phone", value: reservation.phone };
  }
  return null;
}

function ContactDetailCell({ value, icon, type, preferredContact, t }) {
  if (!value) {
    return <span className="ev-res-contact__empty">—</span>;
  }

  const isPreferred = preferredContact === type;

  return (
    <div className={`ev-res-contact__cell${isPreferred ? " is-preferred" : ""}`}>
      <span
        className={`ev-res-contact__item ev-res-contact__item--${type}${
          isPreferred ? " is-preferred" : ""
        }${icon ? "" : " ev-res-contact__item--no-icon"}`}
        title={value}
      >
        {icon ? <i className={icon} aria-hidden /> : null}
        <span>{value}</span>
        <PreferredStar channel={type} preferredContact={preferredContact} t={t} />
      </span>
      <PreferredChannelLabel channel={type} preferredContact={preferredContact} t={t} />
    </div>
  );
}

const buildEmailHref = (email, subject, body) => {
  const clean = String(email || "").trim();
  if (!clean) return "";
  const params = new URLSearchParams({
    view: "cm",
    fs: "1",
    to: clean,
  });
  if (subject) params.set("su", subject);
  if (body) params.set("body", body);
  return `https://mail.google.com/mail/?${params.toString()}`;
};

const buildReservationReplyContent = (reservation, event, t) => {
  const guestName = `${reservation.firstName || ""} ${reservation.lastName || ""}`.trim();
  const eventTitle = event?.title || t("eventReservationPageTitle");
  return {
    whatsappMessage: t("eventReservationWhatsAppReply", {
      name: guestName || t("eventReservationColGuest"),
      event: eventTitle,
    }),
    emailSubject: t("eventReservationEmailSubject", { event: eventTitle }),
    emailBody: t("eventReservationEmailBody", {
      name: guestName || t("eventReservationColGuest"),
      event: eventTitle,
    }),
  };
};

function ReservationContactActions({ reservation, event, t, onArchive, archiving }) {
  const { whatsappMessage, emailSubject, emailBody } = buildReservationReplyContent(reservation, event, t);
  const whatsappHref = buildWhatsAppHref(reservation.whatsapp, whatsappMessage);
  const emailHref = buildEmailHref(reservation.email, emailSubject, emailBody);

  return (
    <div className="ev-res-row-actions">
      <a
        href={whatsappHref || undefined}
        className={`ev-res-contact-action ev-res-contact-action--whatsapp${whatsappHref ? "" : " is-disabled"}`}
        target="_blank"
        rel="noopener noreferrer"
        title={t("eventReservationActionWhatsApp")}
        aria-label={t("eventReservationActionWhatsApp")}
        onClick={(e) => {
          if (!whatsappHref) e.preventDefault();
        }}
      >
        <i className="fa-brands fa-whatsapp" aria-hidden />
      </a>
      <a
        href={emailHref || undefined}
        className={`ev-res-contact-action ev-res-contact-action--email${emailHref ? "" : " is-disabled"}`}
        target="_blank"
        rel="noopener noreferrer"
        title={t("eventReservationActionEmail")}
        aria-label={t("eventReservationActionEmail")}
        onClick={(e) => {
          if (!emailHref) e.preventDefault();
        }}
      >
        <i className="fa-regular fa-envelope" aria-hidden />
      </a>
      <button
        type="button"
        className="ev-res-contact-action ev-res-contact-action--archive"
        title={t("eventReservationArchiveAction")}
        aria-label={t("eventReservationArchiveAction")}
        disabled={archiving}
        onClick={() => onArchive?.(reservation)}
      >
        <i className="fa-solid fa-archive" aria-hidden />
      </button>
    </div>
  );
}

function PlacesCapacityBadge({ placesInfo, t, onEdit, updating }) {
  if (placesInfo.capacity == null) return null;

  const showRemainingCount =
    placesInfo.remainingPlaces != null &&
    (!placesInfo.isFull || (placesInfo.isManuallyFull && placesInfo.remainingPlaces > 0));

  return (
    <div
      className={`ev-res-capacity${placesInfo.isFull ? " ev-res-capacity--full" : ""}${
        placesInfo.isManualRemaining ? " ev-res-capacity--manual" : ""
      }`}
      role="status"
    >
      <span className="ev-res-capacity__badge">
        <i className="fa-solid fa-users" aria-hidden />
        {showRemainingCount
          ? t("eventReservationPlacesRemaining", {
              count: placesInfo.remainingPlaces ?? 0,
            })
          : t("eventReservationPlacesFull")}
        <button
          type="button"
          className="ev-res-capacity__edit"
          onClick={onEdit}
          disabled={updating}
          title={t("eventReservationEditPlaces")}
          aria-label={t("eventReservationEditPlaces")}
        >
          <i className="fa-solid fa-pen-to-square" aria-hidden />
        </button>
      </span>
      <span className="ev-res-capacity__meta">
        {t("eventReservationCapacityMeta", {
          confirmed: placesInfo.confirmedGuests,
          capacity: placesInfo.capacity,
        })}
        {placesInfo.computedRemainingPlaces != null
          ? ` · ${t("eventReservationPlacesAutoValue", {
              count: placesInfo.computedRemainingPlaces,
            })}`
          : null}
      </span>
    </div>
  );
}

function StatusPicker({ reservation, t, isOpen, isUpdating, onToggle, onStatusChange }) {
  const pickerRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);

  const updateMenuPosition = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 168;
    const menuHeight = STATUS_OPTIONS.length * 40 + 16;
    const gap = 6;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openAbove = spaceBelow < menuHeight + gap && rect.top > menuHeight + gap;
    const maxLeft = window.innerWidth - menuWidth - 12;
    const left = Math.min(Math.max(12, rect.left), maxLeft);

    setMenuPosition({
      top: openAbove ? rect.top - gap : rect.bottom + gap,
      left,
      width: Math.max(rect.width, menuWidth),
      transform: openAbove ? "translateY(-100%)" : "none",
    });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setMenuPosition(null);
      return undefined;
    }
    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);
    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, updateMenuPosition]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      const target = event.target;
      if (pickerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      onToggle(false);
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") onToggle(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onToggle]);

  const status = reservation.status;

  const menu =
    isOpen && menuPosition
      ? createPortal(
          <ul
            ref={menuRef}
            className="ev-res-status-menu ev-res-status-menu--portal"
            style={menuPosition}
            role="listbox"
            aria-label={t("eventReservationColStatus")}
          >
            {STATUS_OPTIONS.map((option) => (
              <li key={option} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={option === status}
                  className={`ev-res-status-menu__item ev-res-status-menu__item--${option}${option === status ? " is-active" : ""}`}
                  onClick={() => {
                    onToggle(false);
                    if (option !== status) onStatusChange(reservation, option);
                  }}
                >
                  <span className={`ev-res-status-menu__dot ev-res-status-menu__dot--${option}`} aria-hidden />
                  {t(`eventReservationStatus_${option}`)}
                  {option === status ? (
                    <i className="fa-solid fa-check ev-res-status-menu__check" aria-hidden />
                  ) : null}
                </button>
              </li>
            ))}
          </ul>,
          document.body
        )
      : null;

  return (
    <div className="ev-res-status-picker" ref={pickerRef}>
      <button
        ref={buttonRef}
        type="button"
        className={`ev-res-status ev-res-status--${status} ev-res-status-btn${isOpen ? " is-open" : ""}${isUpdating ? " is-updating" : ""}`}
        onClick={() => !isUpdating && onToggle(!isOpen)}
        disabled={isUpdating}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={t("eventReservationColStatus")}
      >
        <span className="ev-res-status__dot" aria-hidden />
        <span className="ev-res-status-btn__label">{t(`eventReservationStatus_${status}`)}</span>
        <i className="fa-solid fa-chevron-down ev-res-status-btn__caret" aria-hidden />
      </button>
      {menu}
    </div>
  );
}

function EventReservations() {
  const { eventId } = useParams();
  const { t } = useTranslation();

  const [event, setEvent] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    contacted: 0,
    confirmed: 0,
    cancelled: 0,
    total: 0,
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [updatingId, setUpdatingId] = useState("");
  const [archivingId, setArchivingId] = useState("");
  const [openStatusId, setOpenStatusId] = useState(null);
  const [updatingPlaces, setUpdatingPlaces] = useState(false);

  const backPath = event?.archived ? "/archived-events" : "/events";

  const loadReservations = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setLoadError(false);
    try {
      const data = await fetchEventReservations({
        eventId,
        page: currentPage + 1,
        limit: PAGE_SIZE,
        status: statusFilter === "all" ? "" : statusFilter,
        search: searchQuery,
      });
      setEvent(data.event || null);
      setReservations(data.reservations || []);
      setTotalItems(data.total || 0);
      setStatusCounts(data.statusCounts || { pending: 0, contacted: 0, confirmed: 0, cancelled: 0, total: 0 });
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [eventId, currentPage, statusFilter, searchQuery]);

  useEffect(() => {
    loadReservations();
  }, [loadReservations]);

  useEffect(() => {
    let active = true;
    const restaurantIdRef = { current: null };

    const joinRestaurantRoom = () => {
      const id = restaurantIdRef.current;
      if (id != null && id !== "") {
        socket.emit("join-restaurant", String(id));
      }
    };

    const onEventReservationChange = (notif) => {
      if (!active) return;
      const notifEventId = notif?.extraData?.eventId;
      if (eventId && notifEventId && String(notifEventId) !== String(eventId)) {
        return;
      }
      loadReservations();
    };

    const init = async () => {
      try {
        const user = await fetchConnectedUser();
        restaurantIdRef.current = user?.restaurantFK ?? null;
      } catch {
        restaurantIdRef.current = null;
      }
      if (!active) return;
      joinRestaurantRoom();
      socket.on("connect", joinRestaurantRoom);
      socket.on("new-event-reservation", onEventReservationChange);
    };

    init();

    return () => {
      active = false;
      socket.off("connect", joinRestaurantRoom);
      socket.off("new-event-reservation", onEventReservationChange);
    };
  }, [eventId, loadReservations]);

  useEffect(() => {
    setOpenStatusId(null);
  }, [currentPage, statusFilter, searchQuery]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setCurrentPage(0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));

  const placesInfo = useMemo(() => {
    const capacity =
      event?.capacity != null && Number(event.capacity) > 0 ? Number(event.capacity) : null;
    const confirmedGuests = Math.max(0, Math.floor(Number(event?.confirmedGuests) || 0));
    const computedRemainingPlaces =
      capacity != null ? Math.max(0, capacity - confirmedGuests) : null;
    const isManualRemaining = Boolean(event?.isManualRemaining);
    const remainingPlaces = isManualRemaining && event?.remainingPlaces != null
      ? Math.max(0, Math.floor(Number(event.remainingPlaces)))
      : computedRemainingPlaces;
    return {
      capacity,
      confirmedGuests,
      remainingPlaces,
      computedRemainingPlaces,
      isManualRemaining,
      isFull:
        event?.isManuallyFull === true ||
        (capacity != null && remainingPlaces === 0),
      isManuallyFull: event?.isManuallyFull === true,
    };
  }, [event]);

  const kpiItems = useMemo(
    () => [
      { id: "all", tone: "all", label: t("eventReservationKpiTotal"), value: statusCounts.total, icon: "fa-layer-group" },
      { id: "pending", tone: "pending", label: t("eventReservationStatus_pending"), value: statusCounts.pending, icon: "fa-clock" },
      { id: "contacted", tone: "contacted", label: t("eventReservationStatus_contacted"), value: statusCounts.contacted, icon: "fa-comment-dots" },
      { id: "confirmed", tone: "confirmed", label: t("eventReservationStatus_confirmed"), value: statusCounts.confirmed, icon: "fa-circle-check" },
      { id: "cancelled", tone: "cancelled", label: t("eventReservationStatus_cancelled"), value: statusCounts.cancelled, icon: "fa-ban" },
    ],
    [statusCounts, t]
  );

  const handleEditRemainingPlaces = async () => {
    if (!eventId || placesInfo.capacity == null) return;

    const autoValue = placesInfo.computedRemainingPlaces ?? 0;
    const result = await Swal.fire({
      title: t("eventReservationEditPlacesTitle"),
      text: t("eventReservationEditPlacesHint", { count: autoValue }),
      input: "number",
      inputValue: placesInfo.remainingPlaces ?? 0,
      inputAttributes: { min: 0, step: 1 },
      showCancelButton: true,
      showDenyButton: true,
      denyButtonText: t("eventReservationPlacesUseAuto"),
      confirmButtonText: t("eventsSave"),
      cancelButtonText: t("eventsCancel"),
      inputValidator: (value) => {
        const parsed = Number(value);
        if (!Number.isFinite(parsed) || parsed < 0 || !Number.isInteger(parsed)) {
          return t("eventReservationEditPlacesInvalid");
        }
        return undefined;
      },
    });

    if (result.isDismissed) return;

    const manualRemainingPlaces = result.isDenied
      ? null
      : Math.max(0, Math.floor(Number(result.value) || 0));

    let payload = result.isDenied
      ? { manualRemainingPlaces: null, isManuallyFull: false, isAlmostFull: false, ...resetEventFullClientOptions() }
      : manualRemainingPlaces === 0
        ? { manualRemainingPlaces: 0, isManuallyFull: true, isAlmostFull: false }
        : { manualRemainingPlaces, isManuallyFull: false, ...resetEventFullClientOptions() };

    if (!result.isDenied && manualRemainingPlaces === 0) {
      const choice = await promptEventFullClientOptions(t, {
        clientShowPlacesWhenFull: event?.clientShowPlacesWhenFull,
        clientShowReserveWhenFull: event?.clientShowReserveWhenFull,
      });
      if (!choice.confirmed) return;
      payload = {
        ...payload,
        clientShowPlacesWhenFull: choice.clientShowPlacesWhenFull,
        clientShowReserveWhenFull: choice.clientShowReserveWhenFull,
      };
    }

    setUpdatingPlaces(true);
    try {
      await updateEvent(eventId, payload);
      await loadReservations();
      Swal.fire({
        icon: "success",
        title: t("eventReservationPlacesUpdated"),
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("eventsError"),
        text: error?.response?.data?.message || error.message || t("eventReservationPlacesUpdateError"),
      });
    } finally {
      setUpdatingPlaces(false);
    }
  };

  const handleArchiveReservation = async (reservation) => {
    const result = await Swal.fire({
      title: t("eventReservationArchiveConfirm"),
      text: t("eventReservationArchiveConfirmText"),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: t("eventReservationArchiveAction"),
      cancelButtonText: t("cancel"),
    });
    if (!result.isConfirmed) return;

    setArchivingId(reservation._id);
    try {
      await updateEventReservationArchive(reservation._id, true);
      await loadReservations();
      Swal.fire({
        icon: "success",
        title: t("eventReservationArchived"),
        text: t("eventReservationArchivedText"),
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: t("eventsError"),
        text: error?.response?.data?.message || error.message || t("eventReservationArchiveError"),
      });
    } finally {
      setArchivingId("");
    }
  };

  const handleStatusChange = async (reservation, nextStatus) => {
    if (!nextStatus || nextStatus === reservation.status) return;

    const guestName = [reservation.firstName, reservation.lastName].filter(Boolean).join(" ").trim();
    const statusLabel = t(`eventReservationStatus_${nextStatus}`);
    const currentStatus = reservation.status || "pending";
    const result = await confirmReservationStatusChange(t, {
      guestName: guestName || t("eventReservationGuestFallback"),
      statusLabel,
      currentLabel: t(`eventReservationStatus_${currentStatus}`),
    });
    if (!result.isConfirmed) return;

    setUpdatingId(reservation._id);
    try {
      await updateEventReservationStatus(reservation._id, nextStatus);
      await loadReservations();
      showEventActionSuccess(t, {
        title: t("eventReservationUpdated"),
        text: t("eventReservationUpdatedText"),
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: t("eventsError"),
        text: t("eventReservationUpdateError"),
      });
    } finally {
      setUpdatingId("");
    }
  };

  return (
    <div className="events-page ev-res-page">
      <div className="events-hero ev-res-hero">
        <div className="events-hero__glow" aria-hidden />
        <div className="events-hero__main">
          <div className="events-hero__content">
            <Link to={backPath} className="ev-res-back" title={t("eventReservationBack")}>
              <i className="fa-solid fa-arrow-left" aria-hidden />
            </Link>
            <div className="ev-res-hero__icon">
              <i className="fa-solid fa-calendar-check" aria-hidden />
            </div>
            <div className="ev-res-hero__text">
              <p className="ev-res-hero__eyebrow">{t("eventReservationPageEyebrow")}</p>
              <h1 className="events-hero__title">{event?.title || t("eventReservationPageTitle")}</h1>
              {event ? (
                <p className="events-hero__subtitle">
                  {formatDateRange(event.startDate, event.endDate)}
                  {event.archived ? ` · ${t("eventsStatusArchived")}` : ""}
                  {placesInfo.capacity != null ? (
                    <>
                      {" · "}
                      <span
                        className={`ev-res-hero__places${
                          placesInfo.isFull ? " ev-res-hero__places--full" : ""
                        }${placesInfo.isManualRemaining ? " ev-res-hero__places--manual" : ""}`}
                      >
                        {placesInfo.remainingPlaces != null &&
                        (!placesInfo.isFull ||
                          (placesInfo.isManuallyFull && placesInfo.remainingPlaces > 0))
                          ? t("eventReservationPlacesRemaining", {
                              count: placesInfo.remainingPlaces ?? 0,
                            })
                          : t("eventReservationPlacesFull")}
                        <button
                          type="button"
                          className="ev-res-hero__places-edit"
                          onClick={handleEditRemainingPlaces}
                          disabled={updatingPlaces}
                          title={t("eventReservationEditPlaces")}
                          aria-label={t("eventReservationEditPlaces")}
                        >
                          <i className="fa-solid fa-pen-to-square" aria-hidden />
                        </button>
                      </span>
                    </>
                  ) : null}
                </p>
              ) : null}
            </div>
          </div>
        </div>

        <div className="ev-res-kpis">
          {kpiItems.map((item) => (
            <div key={item.id} className={`ev-res-kpi ev-res-kpi--${item.tone}`}>
              <span className="ev-res-kpi__icon">
                <i className={`fa-solid ${item.icon}`} aria-hidden />
              </span>
              <span className="ev-res-kpi__label">{item.label}</span>
              <span className="ev-res-kpi__value">{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="events-filters">
        <div
          className={`events-filters__bar ev-res-filters__bar${
            searchInput || statusFilter !== "all" ? " events-filters__bar--has-clear" : ""
          }`}
        >
          <div className="events-filters__zone events-filters__zone--search">
            <span className="events-filters__label">{t("eventReservationSearch")}</span>
            <div className="events-search events-search--bar">
              <i className="fa-solid fa-magnifying-glass" aria-hidden />
              <input
                type="search"
                placeholder={t("eventReservationSearchPlaceholder")}
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>
          <div className="events-filters__zone events-filters__zone--status">
            <label className="events-filters__label" htmlFor="ev-res-status-filter">
              {t("eventsFilterStatus")}
            </label>
            <div className="events-filter-select-wrap">
              <i className="fa-solid fa-filter" aria-hidden />
              <select
                id="ev-res-status-filter"
                className="events-filter-select"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(0);
                }}
              >
                {RESERVATION_STATUS_FILTER_OPTIONS.map(({ id, labelKey }) => (
                  <option key={id} value={id}>
                    {t(labelKey)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {(searchInput || statusFilter !== "all") && (
            <button
              type="button"
              className="events-btn events-btn--ghost events-btn--sm"
              onClick={() => {
                setSearchInput("");
                setStatusFilter("all");
                setCurrentPage(0);
              }}
            >
              <i className="fa-solid fa-xmark" aria-hidden />
              {t("eventsFilterClear")}
            </button>
          )}
        </div>
      </div>

      <div className="events-table-card ev-res-table-card">
        {loading ? (
          <div className="events-empty">
            <div className="events-empty__icon">
              <i className="fa-solid fa-spinner fa-spin" aria-hidden />
            </div>
            <h2 className="events-empty__title">{t("eventReservationLoading")}</h2>
          </div>
        ) : loadError ? (
          <div className="events-empty">
            <div className="events-empty__icon">
              <i className="fa-solid fa-triangle-exclamation" aria-hidden />
            </div>
            <h2 className="events-empty__title">{t("eventReservationLoadError")}</h2>
            <button type="button" className="events-btn events-btn--primary events-empty__cta" onClick={loadReservations}>
              {t("eventReservationRetry")}
            </button>
          </div>
        ) : reservations.length === 0 ? (
          <div className="events-empty">
            <div className="events-empty__icon">
              <i className="fa-regular fa-calendar-xmark" aria-hidden />
            </div>
            <h2 className="events-empty__title">{t("eventReservationEmpty")}</h2>
            <p className="events-empty__text">{t("eventReservationEmptyText")}</p>
            <Link to={backPath} className="events-btn events-btn--ghost events-empty__cta">
              <i className="fa-solid fa-arrow-left" aria-hidden />
              {t("eventReservationBack")}
            </Link>
          </div>
        ) : (
          <>
            <div className="events-table-card__head ev-res-table-card__head">
              <div className="ev-res-table-card__head-main">
                <span className="events-table-card__title">
                  <i className="fa-solid fa-users" aria-hidden />
                  {t("eventReservationTableTitle")}
                </span>
                <span className="events-table-card__count">
                  {t("eventReservationTableCount", { count: totalItems })}
                </span>
              </div>
              <PlacesCapacityBadge
                placesInfo={placesInfo}
                t={t}
                onEdit={handleEditRemainingPlaces}
                updating={updatingPlaces}
              />
            </div>
            <div className="events-table-wrap ev-res-table-wrap">
              <table className="events-table events-table--modern ev-res-table">
                <thead>
                  <tr>
                    <th>
                      <ResTableTh icon="fa-solid fa-user" label={t("eventReservationColRequestedBy")} />
                    </th>
                    <th>
                      <ResTableTh icon="fa-solid fa-users" label={t("eventReservationColPersons")} />
                    </th>
                    <th>
                      <ResTableTh icon="fa-solid fa-calendar-days" label={t("eventReservationColSlot")} />
                    </th>
                    <th>
                      <ResTableTh
                        icon="fa-brands fa-whatsapp"
                        iconClassName="ev-res-th-label__icon--whatsapp"
                        label={t("eventReservationColWhatsapp")}
                      />
                    </th>
                    <th>
                      <ResTableTh icon="fa-solid fa-phone" label={t("eventReservationColPhone")} />
                    </th>
                    <th>
                      <ResTableTh icon="fa-solid fa-circle-half-stroke" label={t("eventReservationColStatus")} />
                    </th>
                    <th>
                      <ResTableTh icon="fa-solid fa-clock" label={t("eventReservationColSubmitted")} />
                    </th>
                    <th>
                      <ResTableTh icon="fa-solid fa-bolt" label={t("eventReservationColActions")} />
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map((reservation) => {
                    const slot = formatReservationSlot(
                      reservation.reservationDate,
                      reservation.reservationTime
                    );
                    const guestContact = getGuestContactLine(reservation);

                    return (
                      <tr
                        key={reservation._id}
                        className={`events-table-row ev-res-row ev-res-row--${reservation.status}`}
                      >
                        <td className="ev-res-td ev-res-td--guest">
                          <div className="ev-res-guest">
                            <span className="ev-res-guest__avatar" aria-hidden>
                              {getInitials(reservation.firstName, reservation.lastName)}
                            </span>
                            <div className="ev-res-guest__body">
                              <div className="ev-res-guest__name">
                                {reservation.firstName} {reservation.lastName}
                              </div>
                              {guestContact ? (
                                <div
                                  className={`ev-res-guest__contact${
                                    reservation.preferredContact === guestContact.channel
                                      ? " is-preferred"
                                      : ""
                                  }`}
                                >
                                  <div className="ev-res-guest__meta">
                                    <PreferredStar
                                      channel={guestContact.channel}
                                      preferredContact={reservation.preferredContact}
                                      t={t}
                                    />
                                    <span>{guestContact.value}</span>
                                  </div>
                                  <PreferredChannelLabel
                                    channel={guestContact.channel}
                                    preferredContact={reservation.preferredContact}
                                    t={t}
                                  />
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className="ev-res-td ev-res-td--persons">
                          {reservation.numberOfPersons ? reservation.numberOfPersons : "—"}
                        </td>
                        <td className="ev-res-td ev-res-td--slot">
                          <div className="ev-res-slot-text">
                            <span className="ev-res-slot-text__date">{slot.dateLine}</span>
                            {slot.timeLine ? (
                              <span className="ev-res-slot-text__time">{slot.timeLine}</span>
                            ) : null}
                          </div>
                        </td>
                        <td className="ev-res-td ev-res-td--whatsapp">
                          <ContactDetailCell
                            value={reservation.whatsapp}
                            type="whatsapp"
                            preferredContact={reservation.preferredContact}
                            t={t}
                          />
                        </td>
                        <td className="ev-res-td ev-res-td--phone">
                          <ContactDetailCell
                            value={reservation.phone}
                            type="phone"
                            preferredContact={reservation.preferredContact}
                            t={t}
                          />
                        </td>
                        <td className="ev-res-td ev-res-td--status">
                          <StatusPicker
                            reservation={reservation}
                            t={t}
                            isOpen={openStatusId === reservation._id}
                            isUpdating={updatingId === reservation._id}
                            onToggle={(next) => setOpenStatusId(next ? reservation._id : null)}
                            onStatusChange={handleStatusChange}
                          />
                        </td>
                        <td className="ev-res-td ev-res-td--submitted">
                          <span className="ev-res-submitted">{formatDateTime(reservation.createdAt)}</span>
                        </td>
                        <td className="ev-res-td ev-res-td--actions">
                          <ReservationContactActions
                            reservation={reservation}
                            event={event}
                            t={t}
                            onArchive={handleArchiveReservation}
                            archiving={archivingId === reservation._id}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="events-pagination">
              <div className="events-pagination__info">
                {t("eventsPagination", {
                  from: totalItems === 0 ? 0 : currentPage * PAGE_SIZE + 1,
                  to: Math.min((currentPage + 1) * PAGE_SIZE, totalItems),
                  total: totalItems,
                })}
              </div>
              <div className="events-pagination__buttons">
                <button
                  type="button"
                  className="events-page-btn"
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                >
                  <i className="fa-solid fa-chevron-left" aria-hidden />
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    type="button"
                    className={`events-page-btn${currentPage === i ? " is-active" : ""}`}
                    onClick={() => setCurrentPage(i)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  type="button"
                  className="events-page-btn"
                  disabled={currentPage >= totalPages - 1}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                >
                  <i className="fa-solid fa-chevron-right" aria-hidden />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default EventReservations;
